// src/utils/PCMPlayer.js

export default class PCMPlayer {
  constructor(option) {
    this.init(option);
  }

  init(option) {
    const defaultOption = {
      inputCodec: 'Int16',
      channels: 1,
      sampleRate: 8000,
      flushTime: 1000,
      fftSize: 2048
    };

    this.option = Object.assign({}, defaultOption, option);

    // Each buffer source gets a unique ID
    this.nodeIdCounter = 0;
    // Track active node IDs
    this.activeNodeIds = new Set();
    // Timer used to delay onended calls
    this.endTimer = null;

    this.samples = new Float32Array();
    this.interval = setInterval(this.flush.bind(this), this.option.flushTime);
    this.convertValue = this.getConvertValue();
    this.typedArray = this.getTypedArray();

    this.initAudioContext();
    this.bindAudioContextEvent();
  }

  getConvertValue() {
    const inputCodecs = {
      Int8: 128,
      Int16: 32768,
      Int32: 2147483648,
      Float32: 1
    };
    if (!inputCodecs[this.option.inputCodec]) {
      throw new Error('Invalid codec. Use Int8, Int16, Int32, or Float32');
    }
    return inputCodecs[this.option.inputCodec];
  }

  getTypedArray() {
    const typedArrays = {
      Int8: Int8Array,
      Int16: Int16Array,
      Int32: Int32Array,
      Float32: Float32Array
    };
    if (!typedArrays[this.option.inputCodec]) {
      throw new Error('Invalid codec. Use Int8, Int16, Int32, or Float32');
    }
    return typedArrays[this.option.inputCodec];
  }

  initAudioContext() {
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    this.gainNode = this.audioCtx.createGain();
    this.gainNode.gain.value = 0.1;
    this.gainNode.connect(this.audioCtx.destination);
    this.startTime = this.audioCtx.currentTime;

    this.analyserNode = this.audioCtx.createAnalyser();
    this.analyserNode.fftSize = this.option.fftSize;
  }

  static isTypedArray(data) {
    return (
      (data.byteLength && data.buffer && data.buffer.constructor === ArrayBuffer) ||
      data.constructor === ArrayBuffer
    );
  }

  isSupported(data) {
    if (!PCMPlayer.isTypedArray(data)) {
      throw new Error('Please provide ArrayBuffer or a TypedArray');
    }
    return true;
  }

  feed(data) {
    this.isSupported(data);

    // Cancel any pending "end" timer if new data arrives
    if (this.endTimer) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }

    const floatData = this.getFormattedValue(data);
    const tmp = new Float32Array(this.samples.length + floatData.length);
    tmp.set(this.samples, 0);
    tmp.set(floatData, this.samples.length);
    this.samples = tmp;
  }

  getFormattedValue(data) {
    if (data.constructor === ArrayBuffer) {
      data = new this.typedArray(data);
    } else {
      data = new this.typedArray(data.buffer);
    }
    const float32 = new Float32Array(data.length);
    for (let i = 0; i < data.length; i++) {
      float32[i] = data[i] / this.convertValue;
    }
    return float32;
  }

  volume(volume) {
    this.gainNode.gain.value = volume;
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.endTimer) {
      clearTimeout(this.endTimer);
    }

    this.samples = null;
    this.activeNodeIds.clear();

    if (this.audioCtx) {
      this.audioCtx.close();
      this.audioCtx = null;
    }
  }

  flush() {
    if (!this.samples.length) return;

    // If new data arrives, we must cancel any previous end timer
    if (this.endTimer) {
      clearTimeout(this.endTimer);
      this.endTimer = null;
    }

    // Create a node ID
    const nodeId = ++this.nodeIdCounter;
    this.activeNodeIds.add(nodeId);

    const bufferSource = this.audioCtx.createBufferSource();
    if (typeof this.option.onended === 'function') {
      bufferSource.onended = (event) => {
        this.activeNodeIds.delete(nodeId);

        // If no active nodes left, start a 0.5s delay before final "ended"
        if (this.activeNodeIds.size === 0) {
          this.scheduleEnd(bufferSource, event);
        }
      };
    }

    const length = this.samples.length / this.option.channels;
    const audioBuffer = this.audioCtx.createBuffer(
      this.option.channels,
      length,
      this.option.sampleRate
    );

    for (let channel = 0; channel < this.option.channels; channel++) {
      const audioData = audioBuffer.getChannelData(channel);
      let offset = channel;
      let decrement = 50;
      for (let i = 0; i < length; i++) {
        audioData[i] = this.samples[offset];

        // fade-in
        if (i < 50) {
          audioData[i] *= i / 50;
        }
        // fade-out
        if (i >= length - 51) {
          audioData[i] *= decrement-- / 50;
        }
        offset += this.option.channels;
      }
    }

    if (this.startTime < this.audioCtx.currentTime) {
      this.startTime = this.audioCtx.currentTime;
    }
    bufferSource.buffer = audioBuffer;
    bufferSource.connect(this.gainNode);
    bufferSource.connect(this.analyserNode);
    bufferSource.start(this.startTime);

    this.startTime += audioBuffer.duration;
    this.samples = new Float32Array();
  }

  /**
   * Schedules a delayed end event (0.5s) so we don’t end prematurely.
   */
  scheduleEnd(bufferSource, event) {
    // If new data arrives in feed(), we’ll cancel this timer.
    this.endTimer = setTimeout(() => {
      // Double-check that no new nodes have started in this time
      if (this.activeNodeIds.size === 0) {
        // Now it's truly safe to call onended
        this.option.onended(bufferSource, event);
      }
      this.endTimer = null;
    }, 500);
  }

  async pause() {
    if (this.audioCtx) {
      await this.audioCtx.suspend();
    }
  }

  async continue() {
    if (this.audioCtx) {
      await this.audioCtx.resume();
    }
  }

  bindAudioContextEvent() {
    if (typeof this.option.onstatechange === 'function') {
      this.audioCtx.onstatechange = (event) => {
        if (this.audioCtx) {
          this.option.onstatechange(this.audioCtx, event, this.audioCtx.state);
        }
      };
    }
  }
}
